import { useState, useEffect } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { IamRole } from '@bumble/app/__generated__/data-contracts';
import { useAccount } from '@bumble/app/hooks/use-account';
import { Loading } from '@bumble/app/components/Loading';

interface IamUserCreateProps {
  onSubmit: () => void;
}
export const IamUserCreate = ({ onSubmit }: IamUserCreateProps) => {
  const api = useApi();
  const { account } = useAccount();
  const [username, setUsername] = useState<string>('');
  const [role, setRole] = useState<IamRole>(IamRole.Readonly);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [entities, setEntities] = useState<string[]>([]);
  const [name, setName] = useState<string>('');

  const createUserAgent = useMutation(api.createIamUserApiV1CreateIamUserPost, {
    onSuccess() {
      toast.success(`Created IAM user ${username} successfully.`);
      onSubmit();
    },
    onError: apiError,
  });

  const { isLoading, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (entityNames != null && entityNames != undefined) {
      setEntities(entityNames.data || []);
    }
  }, [entityNames]);

  const validateUsername = () => {
    if (/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/g.test(username)) return true;
    toast.error(`Invalid format. Username should be an email.`);
    return false;
  };

  const handleSubmit = () => {
    validateUsername() && createUserAgent.mutate({ username, role, entity_name: selectedEntity, name });
  };

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedEntity(value);
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="username"
            label="User email"
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as IamRole)}
            >
              {Object.values(IamRole).map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="entity-names-label">Entity Names</InputLabel>
            <Select
              labelId="entity-names-label"
              value={selectedEntity}
              label="Entity Names"
              onChange={handleChange}
            >
              { entities && entities.map((item) => {
                return <MenuItem value={item} key={item}>{item}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="name"
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={username === ''}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

IamUserCreate.displayName = 'IamUserCreate';
